import { Action } from '@reduxjs/toolkit'
import {
  clusterRepository,
  ClusterStore,
  clusterStore,
  LAYER_SIZE
} from '@entities/cluster'
import { ProfileStore, profileStore } from '@entities/profile'
import { StateObservable } from 'redux-observable'
import { of, filter, Observable, forkJoin, concat } from 'rxjs'
import { catchError, switchMap, withLatestFrom, finalize } from 'rxjs/operators'
import { registerEpic } from '@shared/lib/redux'
import { RouterStore, routerStore } from '@shared/lib/router'

type PartialRootState = {
  [clusterStore.name]: ClusterStore
  [profileStore.name]: ProfileStore
  [routerStore.name]: RouterStore
}

const getLayerListEpic = (
  action$: Observable<Action>,
  state$: StateObservable<PartialRootState>
) =>
  action$.pipe(
    filter(clusterStore.actions.getClustersStart.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      if (
        !state.profileSlice.profile ||
        !state.profileSlice.profile?.subDays ||
        state.profileSlice.profile.subDays <= 0
      ) {
        return concat(
          of(clusterStore.actions.getClustersCanceled()),
          of(routerStore.actions.saveCurrent('/forbidden'))
        )
      }
      const abortController = new AbortController()
      return forkJoin({
        [LAYER_SIZE.LITTLE]: clusterRepository.getClusterList({
          ...action.payload,
          targetSize: 'small',
          abortSignal: abortController.signal
        }),
        [LAYER_SIZE.MEDIUM]: clusterRepository.getClusterList({
          ...action.payload,
          targetSize: 'medium',
          abortSignal: abortController.signal
        }),
        [LAYER_SIZE.LARGE]: clusterRepository.getClusterList({
          ...action.payload,
          targetSize: 'big',
          abortSignal: abortController.signal
        })
      }).pipe(
        switchMap((clusters) =>
          of(clusterStore.actions.getClustersSuccess(clusters))
        ),
        catchError((error) =>
          of(clusterStore.actions.getClustersFailed(error as string))
        ),
        finalize(() => abortController.abort())
      )
    })
  )

registerEpic(getLayerListEpic)
